import React from 'react';
import { 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  Button,
  Box,
  Typography,
  IconButton,
  Badge,
  LinearProgress
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/PhoneAndroid';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MicIcon from '@mui/icons-material/Mic';
import PeopleIcon from '@mui/icons-material/People';
import FaceIcon from '@mui/icons-material/Face';

const WarningPopup = ({ open, message, onClose, alertType, warningCount = 0, maxWarnings = 10, isTerminated = false }) => {
  // Determine the alert header based on the alert type - exactly like original proctor app
  const getAlertHeader = () => {
    if (isTerminated || warningCount >= maxWarnings) {
      return 'Exam Terminated';
    }

    switch (alertType) {
      case 'AUDIO':
      case 'SPEECH':
      case 'MOUTH':
        return 'Voice/Audio Detected';
      case 'GAZE':
        return 'Eye Movement Detected';
      case 'PERSON':
        return message.includes('Multiple') ? 'Multiple Persons Detected' : 'Candidate Not in Frame';
      case 'PHONE':
        return 'Mobile Phone Detected';
      case 'SPOOF':
        return 'Face Spoofing Detected';
      case 'FOCUS':
        return 'Browser Focus Lost';
      default:
        return 'Warning / Alert!';
    }
  };

  // Get the appropriate icon based on alert type
  const getAlertIcon = () => {
    switch (alertType) {
      case 'AUDIO':
      case 'SPEECH':
      case 'MOUTH':
        return <MicIcon sx={{ mr: 1 }} fontSize="small" />;
      case 'GAZE':
        return <VisibilityOffIcon sx={{ mr: 1 }} fontSize="small" />;
      case 'PERSON':
        return message.includes('Multiple') ? 
          <PeopleIcon sx={{ mr: 1 }} fontSize="small" /> : 
          <FaceIcon sx={{ mr: 1 }} fontSize="small" />;
      case 'PHONE':
        return <PhoneIcon sx={{ mr: 1 }} fontSize="small" />;
      case 'SPOOF':
        return <FaceIcon sx={{ mr: 1 }} fontSize="small" />;
      default:
        return <WarningIcon sx={{ mr: 1 }} fontSize="small" />;
    }
  };

  // Update message to match original proctor app exactly
  const getModifiedMessage = () => {
    switch (alertType) {
      case 'FOCUS':
        return "You have moved out from the Current Exam window. You are not allowed to Move out from Exam page. If you continue to do like this your exam will be terminated.";
      case 'PERSON':
        if (message.includes('Multiple')) {
          return "Multiple persons detected in the camera. Please ensure only you are visible in the camera frame.";
        } else {
          return "Warning: Candidate not detected in the camera. Please ensure your face is clearly visible to continue the exam.";
        }
      case 'PHONE':
        return "Mobile phone detected in the camera. Please remove any mobile devices from the camera view.";
      case 'AUDIO':
      case 'SPEECH':
      case 'MOUTH':
        return "Voice or audio detected during the exam. Please maintain silence during the examination.";
      case 'GAZE':
        return "Suspicious eye movement detected. Please keep your eyes focused on the screen.";
      case 'SPOOF':
        return "Face spoofing attempt detected. Please ensure you are taking the exam yourself.";
      default:
        return message;
    }
  };

  // Calculate warning progress percentage
  const warningProgress = (warningCount / maxWarnings) * 100;
  
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Only allow close via the OK button
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      aria-labelledby="warning-dialog-title"
      aria-describedby="warning-dialog-description"
      disableEscapeKeyDown
      hideBackdrop={false}
      PaperProps={{
        sx: {
          borderRadius: '4px',
          overflow: 'hidden',
          maxWidth: '450px',
          margin: 0
        }
      }}
    >
      <Box sx={{ 
        bgcolor: '#d32f2f', 
        color: 'white',
        p: 1,
        pl: 2,
        pr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getAlertIcon()}
          <Typography variant="subtitle2" component="span">
            {getAlertHeader()}
          </Typography>
        </Box>
      </Box>
      
      <DialogContent sx={{ p: 2, pt: 2, pb: 1 }}>
        <DialogContentText 
          id="warning-dialog-description" 
          sx={{ 
            color: 'text.primary', 
            fontSize: '0.9rem',
            m: 0
          }}
        >
          {getModifiedMessage()}
        </DialogContentText>
        
        {/* Warning count display */}
        {isTerminated || warningCount >= maxWarnings ? (
          <Box sx={{ mt: 2, mb: 0 }}>
            <Typography
              variant="subtitle2"
              color="error"
              fontWeight="bold"
              sx={{
                fontSize: '1rem',
                textAlign: 'center',
                p: 1,
                bgcolor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: 1
              }}
            >
              EXAM TERMINATED - Maximum warnings ({maxWarnings}) reached
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2, mb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2" color="error" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                Warning {warningCount} of {maxWarnings}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                {maxWarnings - warningCount} warnings remaining
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={warningProgress}
              color={warningProgress > 70 ? "error" : "warning"}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}
        
        <Typography
          variant="body2"
          align="center"
          sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}
        >
          {isTerminated || warningCount >= maxWarnings
            ? 'Click OK to acknowledge termination'
            : 'You must click the OK button to continue the exam'}
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 1, justifyContent: 'center' }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="error" 
          size="medium"
          sx={{ 
            textTransform: 'uppercase',
            minWidth: '120px',
            fontSize: '0.85rem',
            py: 1,
            fontWeight: 'bold'
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarningPopup; 